import { MoreVert } from '@mui/icons-material';
import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { TablePaginationActions } from './TablePaginationActions';
import { IUnderwriter } from './types';

interface UnderwriterTableProps {
  underwriters: IUnderwriter[];
  onEditUnderwriter: (underwriter: IUnderwriter) => void;
}

export function UnderwriterTable({
  underwriters,
  onEditUnderwriter,
}: UnderwriterTableProps) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    event?.preventDefault();
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calculate empty rows when needed
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - underwriters.length) : 0;

  return (
    <TableContainer
      sx={{ borderRadius: 2, border: '1px solid var(--neutrals-night-20)' }}
    >
      <Table>
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: 'var(--neutrals-truffle-15)',
              '& th': {
                fontWeight: 500,
              },
            }}
          >
            <TableCell>Name</TableCell>
            <TableCell>Peer Reviewer</TableCell>
            <TableCell align="center" sx={{ width: 100 }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {underwriters.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} align="center">
                No underwriters found
              </TableCell>
            </TableRow>
          ) : (
            (rowsPerPage > 0
              ? underwriters.slice(
                  page * rowsPerPage,
                  page * rowsPerPage + rowsPerPage,
                )
              : underwriters
            ).map(underwriter => (
              <TableRow
                key={underwriter.id}
                sx={{
                  '&:hover': {
                    backgroundColor: theme => theme.palette.action.hover,
                  },
                  '& td': {
                    borderBottom: '1px solid var(--neutrals-night-20)',
                    py: 1.5,
                  },
                }}
              >
                <TableCell>
                  {underwriter.displayName !== null
                    ? underwriter.displayName
                    : underwriter.firstName + ' ' + underwriter.lastName}
                </TableCell>
                <TableCell>
                  {underwriter.peerReviewer ? (
                    underwriter.peerReviewer.displayName ||
                    underwriter.peerReviewer.firstName +
                      ' ' +
                      underwriter.peerReviewer.lastName
                  ) : (
                    <Typography color="textSecondary">-</Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={() => onEditUnderwriter(underwriter)}
                  >
                    <MoreVert fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
          )}
          {emptyRows > 0 && (
            <TableRow style={{ height: 53 * emptyRows }}>
              <TableCell colSpan={6} />
            </TableRow>
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
              colSpan={6}
              count={underwriters.length}
              rowsPerPage={rowsPerPage}
              page={page}
              slotProps={{
                select: {
                  inputProps: {
                    'aria-label': 'rows per page',
                  },
                  native: true,
                },
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}
